/**
 * Lazy mount + LRU keep-alive budget for the *content* of view-instance tabs
 * (架构方案 §5.7 视图缓存).
 *
 * A restored dockview tab exists before anyone looks at it, and its React
 * content used to mount along with the panel — so a page reload rendered every
 * tab that happened to be open last time, iframes and network traffic included,
 * for documents the user may never come back to. The rail already had the right
 * presentation for this (`EditorSlot` + `editor-cache`): mount on first reveal,
 * keep the tree alive afterwards while it is worth keeping, and evict LRU over
 * an explicit budget. This is that policy applied to the tab strips.
 *
 * One MRU list of mounted instance ids is shared by every tabbed area, on
 * purpose rather than one cache per host: a tab can be dragged from the editor
 * into the auxiliaryBar, so per-area attribution goes stale the moment it moves,
 * and «how many off-screen tab contents may the workbench keep» reads better as
 * one budget than as two. Several ids are on screen at once (one per group) —
 * those are always mounted and never count against the budget.
 *
 * The list is the same shape as the editor MRU cache and evicts the same way:
 * eviction = unmount = the existing `deactivateView` teardown chain (§5.4), so
 * an evicted tab costs a reload when it is next clicked, exactly like a tab that
 * was never opened in this session.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useSyncExternalStore,
} from 'react';

import { sameInstanceList } from '../editor/editor-cache';

/** Max mounted-but-off-screen tab contents before LRU eviction. */
export const TAB_CACHE_CAPACITY = 3;

/**
 * Next mounted set from the current MRU list (head = most recently revealed).
 * On-screen ids are kept unconditionally; off-screen ones survive only while
 * `isCacheable` holds and only up to `capacity`, dropping the least recently
 * revealed first. Mirrors `planMountedInstances` (§5.7 视图缓存), generalized
 * from one active instance to the set that is on screen — a workbench shows one
 * tab per tabbed area, not one overall.
 *
 * Pure and idempotent: an id absent from `mounted` is one that has never been
 * revealed, and nothing here can admit it.
 */
export function planTabContents(
  mounted: readonly string[],
  onScreen: ReadonlySet<string>,
  isCacheable: (instanceId: string) => boolean,
  capacity: number = TAB_CACHE_CAPACITY,
): readonly string[] {
  const visible = mounted.filter((id) => onScreen.has(id));
  const hidden = mounted
    .filter((id) => !onScreen.has(id) && isCacheable(id))
    .slice(0, capacity);
  return [...visible, ...hidden];
}

/** What a live tab panel reports about itself (see {@link TabContentCache}). */
export interface TabContentState {
  /** The panel is the active one of a visible group, i.e. actually on screen. */
  readonly onScreen: boolean;
  /** Its view's effective policy is `keep-alive` (`resolveViewCachePolicy`). */
  readonly keepAlive: boolean;
}

/**
 * Observable MRU store behind {@link planTabContents}. Owned by the workbench
 * layout (one per window) and consumed by tab panels through
 * {@link useTabContentMounted}.
 */
export class TabContentCache {
  /** Live tab panels, whether or not their content is mounted. */
  private readonly panels = new Map<string, TabContentState>();
  private mounted: readonly string[] = [];
  private readonly listeners = new Set<() => void>();

  constructor(private readonly capacity: number = TAB_CACHE_CAPACITY) {}

  isMounted(instanceId: string): boolean {
    return this.mounted.includes(instanceId);
  }

  /**
   * Report a panel's current state. Being on screen is what *admits* an id to
   * the cache — that is the «opened at least once» gate — and also touches it
   * to the head, which is what makes the eviction order LRU.
   */
  sync(instanceId: string, state: TabContentState): void {
    this.panels.set(instanceId, state);
    this.commit(
      state.onScreen
        ? [instanceId, ...this.mounted.filter((id) => id !== instanceId)]
        : this.mounted,
    );
  }

  /** The panel itself is gone (tab closed, layout rebuilt): free its slot. */
  release(instanceId: string): void {
    this.panels.delete(instanceId);
    this.commit(this.mounted.filter((id) => id !== instanceId));
  }

  /**
   * Drop every off-screen content (§5.7 视图缓存 的保守驱逐). Same trigger as
   * the editor cache: a dynamically updated module can keep its view ids while
   * its UI bundle changed (见 12/17), and a kept-alive tree from the previous
   * bundle must not survive that.
   */
  evictOffScreen(): void {
    this.commit(
      this.mounted.filter((id) => this.panels.get(id)?.onScreen === true),
    );
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private commit(candidate: readonly string[]): void {
    const onScreen = new Set<string>();
    for (const [instanceId, state] of this.panels) {
      if (state.onScreen) {
        onScreen.add(instanceId);
      }
    }
    const planned = planTabContents(
      candidate,
      onScreen,
      (instanceId) => this.panels.get(instanceId)?.keepAlive === true,
      this.capacity,
    );
    if (sameInstanceList(planned, this.mounted)) {
      return;
    }
    this.mounted = planned;
    for (const listener of this.listeners) {
      listener();
    }
  }
}

/**
 * The workbench's cache. The default instance keeps a tab panel rendered in
 * isolation (tests, a future non-workbench dockview) working with its own
 * budget instead of needing null checks on every read.
 */
export const TabContentCacheContext = createContext<TabContentCache>(
  new TabContentCache(),
);

/**
 * Whether this tab's content should be mounted right now (§5.7 视图缓存).
 *
 * `onScreen` wins synchronously instead of waiting for the effect below to
 * reach the store: the tab the user just clicked has to paint in the same
 * commit, and a store round-trip would blank it for a frame.
 */
export function useTabContentMounted(
  instanceId: string,
  onScreen: boolean,
  keepAlive: boolean,
): boolean {
  const cache = useContext(TabContentCacheContext);
  const cached = useSyncExternalStore(
    useCallback((listener: () => void) => cache.subscribe(listener), [cache]),
    useCallback(() => cache.isMounted(instanceId), [cache, instanceId]),
  );

  useEffect(() => {
    cache.sync(instanceId, { onScreen, keepAlive });
  }, [cache, instanceId, onScreen, keepAlive]);

  useEffect(
    () => () => {
      cache.release(instanceId);
    },
    [cache, instanceId],
  );

  return onScreen || cached;
}
